import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { INVITE_INTRO } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_GROUPS = [
  {
    label: "Worship Leading",
    options: [
      "Sunday Morning Services",
      "Night of Worship",
      "Conference / Retreat Worship",
      "Youth / Student Event",
      "Special Event",
      "Other Worship Opportunity",
    ],
  },
  {
    label: "Speaking, Teaching & Development",
    options: [
      "Worship Team Workshop / Training",
      "Worship Leadership Coaching",
      "Speaking / Teaching",
      "Conference / Breakout Session",
      "Leadership Development",
      "Other Speaking / Training Opportunity",
    ],
  },
];

const TEAM_OPTIONS = [
  "David solo",
  "David + simple team",
  "David + full team",
  "David leading with our church/host team",
  "Not sure — let's discuss",
];

const schema = z.object({
  name: z.string().min(2, "Please share your name"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().max(40).optional().or(z.literal("")),
  organization: z.string().min(2, "Church or organization is required"),
  city_state: z.string().min(2, "City and state are required"),
  event_date: z.string().max(80).optional().or(z.literal("")),
  dates_flexible: z.boolean().optional(),
  attendance: z.string().max(40).optional().or(z.literal("")),
  services: z.array(z.string()).min(1, "Please choose at least one way David can serve"),
  team_arrangement: z.string().max(80).optional().or(z.literal("")),
  details: z.string().max(4000).optional().or(z.literal("")),
  website: z.string().max(0).optional().or(z.literal("")),
});

const inputCls =
  "w-full bg-transparent border border-ink/25 px-4 py-3 text-sm text-ink placeholder:text-fog/70 focus:border-ink focus:outline-none transition-colors duration-300";
const labelCls = "block text-[11px] font-semibold tracking-[0.18em] uppercase text-charcoal mb-2";
const errCls = "mt-1.5 text-xs text-red-800";
const checkCls =
  "flex items-center gap-3 border border-ink/25 px-4 py-3 cursor-pointer has-[:checked]:border-ink has-[:checked]:bg-ink has-[:checked]:text-paper transition-colors duration-300";

export default function Invite() {
  const [status, setStatus] = useState("idle");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { services: [], team_arrangement: "", website: "", dates_flexible: false },
  });

  const datesFlexible = watch("dates_flexible");

  const onSubmit = async (values) => {
    setStatus("submitting");
    const payload = { ...values };
    if (payload.dates_flexible) payload.event_date = "Flexible / not yet determined";
    delete payload.dates_flexible;
    try {
      await axios.post(`${API}/booking`, payload);
      setStatus("success");
    } catch (e) {
      setStatus("error");
      toast.error(
        e.response?.data?.detail || "Something went wrong sending your inquiry. Please try again."
      );
    }
  };

  return (
    <section id="invite" data-testid="invite-section" className="bg-ink text-paper py-24 md:py-36 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <SectionHeading number="02" title="Invite David to Serve" dark testId="invite-heading" />

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p data-testid="invite-intro" className="text-base md:text-lg leading-relaxed text-paper/80 max-w-xl">
                {INVITE_INTRO}
              </p>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-7" delay={0.15}>
            {status === "success" ? (
              <div
                data-testid="booking-success-message"
                role="status"
                className="bg-paper text-ink p-10 md:p-14 flex flex-col items-start gap-5"
              >
                <CheckCircle2 size={32} aria-hidden="true" />
                <p className="font-spartan font-bold text-2xl md:text-3xl leading-snug max-w-md">
                  Thank you for reaching out. Your inquiry has been received, and David’s team
                  will respond within 2–3 days.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="booking-form"
                noValidate
                className="bg-paper text-ink p-8 md:p-12"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7">
                  <div>
                    <label htmlFor="bk-name" className={labelCls}>Name *</label>
                    <input id="bk-name" data-testid="booking-field-name" autoComplete="name" className={inputCls} {...register("name")} />
                    {errors.name && <p className={errCls} role="alert">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-email" className={labelCls}>Email *</label>
                    <input id="bk-email" data-testid="booking-field-email" type="email" autoComplete="email" className={inputCls} {...register("email")} />
                    {errors.email && <p className={errCls} role="alert">{errors.email.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-phone" className={labelCls}>Phone number</label>
                    <input id="bk-phone" data-testid="booking-field-phone" type="tel" autoComplete="tel" className={inputCls} {...register("phone")} />
                  </div>
                  <div>
                    <label htmlFor="bk-org" className={labelCls}>Church or organization *</label>
                    <input id="bk-org" data-testid="booking-field-organization" autoComplete="organization" className={inputCls} {...register("organization")} />
                    {errors.organization && <p className={errCls} role="alert">{errors.organization.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-city" className={labelCls}>City and state *</label>
                    <input id="bk-city" data-testid="booking-field-city-state" placeholder="e.g. Franklin, TN" className={inputCls} {...register("city_state")} />
                    {errors.city_state && <p className={errCls} role="alert">{errors.city_state.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="bk-att" className={labelCls}>Estimated attendance</label>
                    <input id="bk-att" data-testid="booking-field-attendance" inputMode="numeric" placeholder="e.g. 300" className={inputCls} {...register("attendance")} />
                  </div>
                </div>

                <fieldset className="mt-10">
                  <legend className="font-spartan font-bold uppercase tracking-tight text-xl md:text-2xl">
                    How would you like David to serve? *
                  </legend>
                  <p className="mt-2 text-sm text-fog">
                    Select as many as apply. Many invitations include a combination of worship
                    leading, speaking, and team development.
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8" data-testid="booking-field-services">
                    {SERVICE_GROUPS.map((group) => (
                      <div key={group.label}>
                        <p className={labelCls}>{group.label}</p>
                        <div className="space-y-3">
                          {group.options.map((option) => (
                            <label key={option} className={checkCls}>
                              <input
                                type="checkbox"
                                value={option}
                                data-testid={`booking-service-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="accent-current w-4 h-4 shrink-0"
                                {...register("services")}
                              />
                              <span className="text-sm">{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.services && <p className={errCls} role="alert">{errors.services.message}</p>}
                </fieldset>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-7 items-end">
                  <div>
                    <label htmlFor="bk-date" className={labelCls}>Event date or potential dates</label>
                    <input
                      id="bk-date"
                      data-testid="booking-field-event-date"
                      placeholder="e.g. March 2027"
                      disabled={datesFlexible}
                      className={`${inputCls} disabled:opacity-40`}
                      {...register("event_date")}
                    />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer pb-3">
                    <input
                      type="checkbox"
                      data-testid="booking-dates-flexible"
                      className="accent-ink w-4 h-4 shrink-0"
                      {...register("dates_flexible")}
                    />
                    <span className="text-sm">Our dates are flexible or not yet determined</span>
                  </label>
                </div>

                <fieldset className="mt-8">
                  <legend className={labelCls}>Team arrangement (if worship leading)</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="booking-field-team-arrangement">
                    {TEAM_OPTIONS.map((option) => (
                      <label key={option} className={checkCls}>
                        <input
                          type="radio"
                          value={option}
                          data-testid={`booking-team-${TEAM_OPTIONS.indexOf(option)}`}
                          className="accent-current"
                          {...register("team_arrangement")}
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="mt-8">
                  <label htmlFor="bk-details" className={labelCls}>
                    Tell us about your church, event, or what you have in mind
                  </label>
                  <textarea
                    id="bk-details"
                    data-testid="booking-field-details"
                    rows={5}
                    className={`${inputCls} resize-y`}
                    {...register("details")}
                  />
                </div>

                <div aria-hidden="true" className="absolute opacity-0 pointer-events-none h-0 overflow-hidden">
                  <label htmlFor="bk-website">Website</label>
                  <input id="bk-website" type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
                </div>

                {status === "error" && (
                  <p data-testid="booking-error-message" role="alert" className="mt-6 text-sm text-red-800">
                    Your inquiry couldn’t be sent. Please try again — or email us directly at{" "}
                    <a href="mailto:davidalanmusic@gmail.com" className="underline">davidalanmusic@gmail.com</a>.
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  disabled={status === "submitting"}
                  className="mt-10 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-ink text-paper font-semibold text-xs tracking-[0.22em] uppercase px-10 py-4 rounded-full hover:bg-charcoal disabled:opacity-60 transition-colors duration-300"
                >
                  {status === "submitting" && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                  {status === "submitting" ? "Sending…" : "Send inquiry"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
